import React, { useMemo } from 'react';
import { TRACKERS, TRACKER_KEYS } from './config/trackers';
import { BELONGS_META } from './config/belongs';
import { formatMoney } from './utils/cells';
import {
    deriveBelongsCard,
    deriveTrackerCard,
    totalPaidForAccount,
    pickMonthlyEstimate,
} from './utils/derive';

/**
 * Belongs drilldown — opens when user taps a quick-view shortcut on the
 * dashboard. Mirrors the v4 mockup's belongs page:
 *   - Header (back button + title + sub paragraph)
 *   - Green gradient hero (eyebrow + title + 3 stats + alert pills)
 *   - 2-up tracker cards (per-tracker breakdown for this category)
 *   - Vertical "All Locations" list
 *
 * Tapping a tracker card pre-filters TrackerDetail to this Belongs key.
 */
function BelongsView({ belongsKey, accounts, onOpenTracker }) {
    const meta = BELONGS_META[belongsKey];
    const filtered = useMemo(
        () => accounts.filter(
            (a) => a.belongs === belongsKey && a.status === 'active',
        ),
        [accounts, belongsKey],
    );
    const stat = useMemo(
        () => deriveBelongsCard(belongsKey, accounts),
        [belongsKey, accounts],
    );

    // Aggregate verified vs unverified across this category.
    const verifiedStats = useMemo(() => {
        let verified = 0;
        let unverified = 0;
        let paidYtd = 0;
        filtered.forEach((row) => {
            const tracker = TRACKERS[row.trackerKey];
            if (!tracker) return;
            const cells = tracker.frequency === 'annual'
                ? row.subTaxes
                : row.months;
            paidYtd += totalPaidForAccount(row);
            (cells || []).forEach((raw) => {
                const str = (raw || '').toString();
                if (str.startsWith('paid:') || str.startsWith('multi:')) {
                    if (/[:;]1(?=$|[:;])/.test(str)) verified += 1;
                    else unverified += 1;
                }
            });
        });
        return { verified, unverified, paidYtd };
    }, [filtered]);

    const monthlyOut = stat.monthlyOut || 0;

    const perTracker = useMemo(() => {
        return TRACKER_KEYS
            .map((k) => {
                const rows = filtered.filter((a) => a.trackerKey === k);
                if (rows.length === 0) return null;
                const card = deriveTrackerCard(k, accounts);
                const total = rows.reduce(
                    (sum, r) => sum + (r.incoming ? 0 : pickMonthlyEstimate(r)),
                    0,
                );
                return { trackerKey: k, count: rows.length, monthly: total, card };
            })
            .filter(Boolean);
    }, [filtered, accounts]);

    return (
        <>

            {/* Green gradient hero */}
            <div className="bt-belongs-hero">
                <div className="bt-belongs-hero-eyebrow">
                    Quick View · {meta.label}
                </div>
                <div className="bt-belongs-hero-title">
                    {stat.count || 0} account{stat.count === 1 ? '' : 's'}
                    {monthlyOut > 0 ? ` · ${formatMoney(monthlyOut)}/mo` : ''}
                </div>
                <div className="bt-belongs-hero-grid">
                    <div className="bt-belongs-hero-stat">
                        <div className="bt-belongs-hero-stat-label">Paid YTD</div>
                        <div className="bt-belongs-hero-stat-value">
                            {formatMoney(verifiedStats.paidYtd)}
                        </div>
                    </div>
                    <div className="bt-belongs-hero-stat">
                        <div className="bt-belongs-hero-stat-label">
                            <span role="img" aria-label="Verified">✓✓</span> Verified
                        </div>
                        <div className="bt-belongs-hero-stat-value">{verifiedStats.verified}</div>
                    </div>
                    <div className="bt-belongs-hero-stat">
                        <div className="bt-belongs-hero-stat-label">
                            <span role="img" aria-label="Unverified">⏱</span> Unverified
                        </div>
                        <div className="bt-belongs-hero-stat-value">{verifiedStats.unverified}</div>
                    </div>
                </div>
                {(stat.overdue > 0 || stat.pending > 0) && (
                    <div className="bt-belongs-hero-alerts">
                        {stat.overdue > 0 && (
                            <div className="bt-belongs-hero-alert urgent">
                                <span role="img" aria-label="Urgent">⚠</span> {stat.overdue} urgent
                            </div>
                        )}
                        {stat.pending > 0 && (
                            <div className="bt-belongs-hero-alert warn">
                                {stat.pending} pending
                            </div>
                        )}
                    </div>
                )}
            </div>

            {perTracker.length === 0 ? (
                <div className="bt-empty">
                    No accounts assigned to {meta.label} yet.
                </div>
            ) : (
                <>
                    <div className="bt-section-label">Breakdown by Tracker</div>
                    <div className="bt-belongs-trackers">
                        {perTracker.map(({ trackerKey, count, monthly, card }) => {
                            const tracker = TRACKERS[trackerKey];
                            let statusNode = null;
                            if (card.cardClass === 'alert') {
                                statusNode = (
                                    <div className="bt-belongs-tracker-status urgent">
                                        <span role="img" aria-label="Urgent">⚠</span> {card.badge.text}
                                    </div>
                                );
                            } else if (card.cardClass === 'warn') {
                                statusNode = (
                                    <div className="bt-belongs-tracker-status warn">
                                        {card.badge.text}
                                    </div>
                                );
                            } else {
                                statusNode = (
                                    <div className="bt-belongs-tracker-status ok">
                                        On track
                                    </div>
                                );
                            }
                            return (
                                <div
                                    key={trackerKey}
                                    className="bt-belongs-tracker-card"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onOpenTracker(trackerKey, belongsKey)}
                                    onKeyDown={(e) => e.key === 'Enter' && onOpenTracker(trackerKey, belongsKey)}
                                >
                                    <div className="bt-belongs-tracker-head">
                                        <span className="bt-belongs-tracker-icon" role="img" aria-label={tracker.label}>
                                            {tracker.icon}
                                        </span>
                                        <span className="bt-belongs-tracker-name">{tracker.label}</span>
                                    </div>
                                    <div className="bt-belongs-tracker-count">{count}</div>
                                    <div className="bt-belongs-tracker-label">
                                        account{count === 1 ? '' : 's'}
                                    </div>
                                    {monthly > 0 && (
                                        <div className="bt-belongs-tracker-monthly">
                                            {formatMoney(monthly)}/mo
                                        </div>
                                    )}
                                    {statusNode}
                                </div>
                            );
                        })}
                    </div>

                    <div className="bt-section-label">All Locations</div>
                    <div className="bt-belongs-locations">
                        {filtered.map((row) => {
                            const t = TRACKERS[row.trackerKey];
                            const monthly = pickMonthlyEstimate(row);
                            return (
                                <button
                                    key={row.accountId}
                                    type="button"
                                    className="bt-belongs-location-card"
                                    onClick={() => onOpenTracker(row.trackerKey, belongsKey)}
                                >
                                    <div className="bt-belongs-location-row">
                                        <span className="bt-belongs-location-icon" role="img" aria-label={t?.label || 'tracker'}>
                                            {t?.icon}
                                        </span>
                                        <div className="bt-belongs-location-info">
                                            <div className="bt-belongs-location-name">{row.label}</div>
                                            <div className="bt-belongs-location-tag">
                                                {t?.label} · {row.status === 'cancelled' ? 'Cancelled' : 'Active'}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="bt-belongs-location-amt-label">
                                                {row.incoming ? 'Incoming' : 'Monthly'}
                                            </div>
                                            <div className="bt-belongs-location-amt">
                                                {monthly > 0 ? formatMoney(monthly) : '—'}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </>
    );
}

export default BelongsView;
